import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import clone from 'ramda/src/clone';
import findIndex from 'ramda/src/findIndex';
import propEq from 'ramda/src/propEq';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Input from '@components/@core/Input';
import { VendorActions } from '~/actions';
import { TIMEZONES } from '~/services/constants';
import { getCompanyName } from '~/services/util';

const Edit = ({ business, avatar, cover, updated, onCancel }) => {
    const dispatch = useDispatch();
    const profile = business?.data?.profile || business?.hcms?.data?.profile;
    const [name, setName] = useState(getCompanyName(business));
    const [description, setDescription] = useState(profile?.about || business?.vrs?.data?.description || 'Not Provided');
    const [phone, setPhone] = useState(profile?.primary_phone || business?.cell_phone || '');
    const [website, setWebsite] = useState(profile?.url || '');
    const [city, setCity] = useState(profile?.address?.city || '');
    const [state, setState] = useState(profile?.address?.state || '');
    const [street, setStreet] = useState(profile?.address?.street || '');
    const [zip, setZip] = useState(profile?.address?.zip || '');
    const [timezone, setTimezone] = useState(business?.data?.timezone || '');
    const timeIdx = timezone ? findIndex(propEq('value', timezone), TIMEZONES) : 0;
    const sponsoredLevel = business?.vrs?.data?.sponsored_level || 'wiki';
    const disabled =
        !updated &&
        name === getCompanyName(business) &&
        description === (profile?.about ?? '') &&
        phone === ((profile?.primary_phone || business?.cell_phone) ?? '') &&
        website == (profile?.url ?? '') &&
        state === (profile?.address?.state ?? '') &&
        city === (profile?.address?.city ?? '') &&
        street === (profile?.address?.street ?? '') &&
        zip === (profile?.address?.zip ?? '') &&
        timezone === (business?.data?.timezone ?? '');

    const handleSave = () => {
        let compInfo = clone(business);
        delete compInfo.admins;
        delete compInfo.blogs;
        delete compInfo.business;
        const payload = {
            data: {
                business: {
                    id: compInfo.id,
                    name,
                    data: {
                        ...compInfo.data,
                        profile: {
                            ...compInfo.data?.profile,
                            address: { city, state, street, zip },
                            about: description,
                            avatar,
                            cover,
                            url: website,
                            primary_phone: phone,
                        },
                        timezone,
                    },
                },
            },
            type: compInfo.slug ? 'vr' : 'hcm',
            after: {
                type: 'GETBUSINESS_REQUEST',
                payload: { id: compInfo.id },
            },
        };
        dispatch(VendorActions.savevrcompanyRequest(payload, onCancel));
    };

    return (
        <ErrorBoundary className="company-profile-about">
            <div className="card pb-1">
                <p className="dsl-b24 bold">Edit Company Information</p>
                <Row className="mx-0">
                    <Col xs={12} sm={6} className="pl-0">
                        <Input className="mb-3" title="Name" value={name} onChange={(e) => setName(e)} />
                        <Input
                            className="align-items-start info"
                            title="Info"
                            as="textarea"
                            maxLength={sponsoredLevel === 'wiki' ? 500 : 0}
                            rows={7}
                            value={description}
                            onChange={(e) => setDescription(e)}
                        />
                    </Col>
                    <Col xs={12} sm={6} className="pl-0">
                        <Input className="mb-3" title="Phone" value={phone} placeholder="000 333 4455" pattern={/[^0-9.]/g} onChange={(e) => setPhone(e)} />
                        <Input className="mb-3" title="Web" value={website} onChange={(e) => setWebsite(e)} />
                        <Input className="align-items-start mb-2" title="Street" value={street} onChange={(e) => setStreet(e)} />
                        <Input className="align-items-start mb-2" title="City" value={city} onChange={(e) => setCity(e)} />
                        <Input className="align-items-start mb-2" title="State" value={state} onChange={(e) => setState(e)} />
                        <Input className="align-items-start mb-3" title="Zip code" value={zip} onChange={(e) => setZip(e)} />
                        <Dropdown
                            className="mb-5"
                            title="TimeZone"
                            width="fit-content"
                            data={TIMEZONES}
                            getValue={(e) => e.label}
                            returnBy="data"
                            defaultIndexes={[timeIdx]}
                            onChange={(e) => setTimezone(e[0].value)}
                        />
                    </Col>
                </Row>
                <div className="d-h-end my-3">
                    <Button className="mr-3" type="low" name="DISCARD" onClick={onCancel} />
                    <Button name="SAVE" disabled={disabled} onClick={handleSave} />
                </div>
            </div>
        </ErrorBoundary>
    );
};

Edit.propTypes = {
    company: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        admins: PropTypes.array,
        business: PropTypes.shape({
            data: PropTypes.array,
        }),
        data: PropTypes.shape({
            contact: PropTypes.shape({
                email: PropTypes.string,
                phone: PropTypes.string,
            }),
            description: PropTypes.string,
        }),
        entity: PropTypes.shape({
            id: PropTypes.number,
            content_id: PropTypes.number,
            group_id: PropTypes.number,
        }),
        products: PropTypes.array,
        stats: PropTypes.shape({
            comments: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            dislikes: PropTypes.number,
            like_avg: PropTypes.string,
            likes: PropTypes.string,
            rating_avg: PropTypes.number,
            rating_count: PropTypes.number,
            rating_score: PropTypes.string,
            views: PropTypes.number,
        }),
        university: PropTypes.array,
    }),
    avatar: PropTypes.string,
    cover: PropTypes.string,
    onCancel: PropTypes.func,
};

Edit.defaultProps = {
    company: {
        id: 0,
        name: '',
        admins: [],
        business: { data: [] },
        data: { contact: { email: '', phone: '' }, description: '' },
        entity: { id: 0, content_id: 0, group_id: 0 },
        products: [],
        stats: {
            comments: '',
            dislikes: 0,
            like_avg: '',
            likes: '',
            rating_avg: 0,
            rating_count: 0,
            rating_score: '',
            views: 0,
        },
        university: [],
    },
    avatar: '',
    cover: '',
    onCancel: () => {},
};

export default Edit;
